import CloseIcon from '@mui/icons-material/Close';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useId, useRef, useState, type KeyboardEvent } from 'react';

/** Livelli di zoom: 1 = piena risoluzione, un pixel dell'immagine per pixel CSS. */
const ZOOM_LEVELS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4] as const;

/** `fit` = adattata alla finestra (mai ingrandita oltre la piena risoluzione). */
type Zoom = 'fit' | number;

export interface ScreenshotViewerProps {
  /** URL `blob:` del contenuto scaricato: mai l'URL dell'API (il file non si apre sulla stessa origine). */
  src: string;
  /** Codice del ticket e schermata segnalata, per il sottotitolo e il testo alternativo. */
  code: string;
  screenLabel: string | null;
  onClose: () => void;
}

const EPSILON = 1e-6;

const percent = (zoom: number) => `${Math.round(zoom * 100)}%`;

/**
 * Visore della schermata catturata all'invio (story #8): si apre adattata alla finestra, poi zoom
 * per livelli fino alla piena risoluzione e oltre; l'area dell'immagine scorre ed è raggiungibile da
 * tastiera (frecce), con le scorciatoie `+`, `-` e `0`. Esc chiude e il focus torna al comando che
 * l'ha aperto (Dialog di MUI). Chi lo apre revoca l'URL `blob:` alla chiusura. Un file che il
 * browser non riesce a decodificare è dichiarato «non leggibile».
 */
export function ScreenshotViewer({ src, code, screenLabel, onClose }: ScreenshotViewerProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const titleId = useId();
  const [zoom, setZoom] = useState<Zoom>('fit');
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const areaRef = useRef<HTMLDivElement>(null);

  const [fitScale, setFitScale] = useState(1);
  const [unreadable, setUnreadable] = useState(false);

  /** Misura (e ricorda) la scala della vista adattata; 1 se non misurabile (immagine non caricata). */
  const measureFit = (size = natural) => {
    const area = areaRef.current;
    let scale = 1;
    if (size && area && size.width > 0 && area.clientWidth > 0) {
      const heightRatio = size.height > 0 && area.clientHeight > 0 ? area.clientHeight / size.height : 1;
      scale = Math.min(1, area.clientWidth / size.width, heightRatio);
    }
    setFitScale(scale);
    return scale;
  };
  const current = () => (zoom === 'fit' ? measureFit() : zoom);
  // Nessun livello oltre gli estremi: «Riduci» da una vista adattata già sotto il 25% non ingrandisce.
  const zoomIn = () => {
    const next = ZOOM_LEVELS.find((level) => level > current() + EPSILON);
    if (next !== undefined) setZoom(next);
  };
  const zoomOut = () => {
    const next = [...ZOOM_LEVELS].reverse().find((level) => level < current() - EPSILON);
    if (next !== undefined) setZoom(next);
  };
  const shown = zoom === 'fit' ? fitScale : zoom;
  const canZoomIn = !unreadable && shown < ZOOM_LEVELS.at(-1)! - EPSILON;
  const canZoomOut = !unreadable && shown > ZOOM_LEVELS[0] + EPSILON;

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.key === '+' || event.key === '=') zoomIn();
    else if (event.key === '-') zoomOut();
    else if (event.key === '0') setZoom('fit');
    else return;
    event.preventDefault();
  };

  const zoomLabel = zoom === 'fit' ? 'Adattata alla finestra' : percent(zoom);

  return (
    <Dialog
      open
      onClose={onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xl"
      aria-labelledby={titleId}
      // Sul Dialog: le scorciatoie valgono appena si apre, ovunque sia il focus dentro il visore.
      onKeyDown={onKeyDown}
      slotProps={{ paper: { sx: fullScreen ? undefined : { height: '90vh' } } }}
    >
      <DialogTitle id={titleId} sx={{ pr: 14 }}>
        Schermata catturata all'invio
        <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'block' }}>
          {code}
          {screenLabel ? ` · ${screenLabel}` : ''}
          {natural ? ` · ${natural.width} × ${natural.height} px` : ''}
        </Typography>
      </DialogTitle>
      <IconButton aria-label="Chiudi il visore" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3, p: 0 }}>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          role="toolbar"
          aria-label="Zoom della schermata"
          sx={{ px: 4, pt: 3, flexWrap: 'wrap', rowGap: 2 }}
        >
          <Button size="small" startIcon={<ZoomOutIcon />} onClick={zoomOut} disabled={!canZoomOut}>
            Riduci
          </Button>
          <Button size="small" startIcon={<ZoomInIcon />} onClick={zoomIn} disabled={!canZoomIn}>
            Ingrandisci
          </Button>
          <Button
            size="small"
            startIcon={<FitScreenIcon />}
            onClick={() => setZoom('fit')}
            aria-pressed={zoom === 'fit'}
          >
            Adatta alla finestra
          </Button>
          <Button size="small" onClick={() => setZoom(1)} aria-pressed={zoom === 1}>
            Piena risoluzione
          </Button>
          <Typography component="output" variant="body2" aria-live="polite" sx={{ ml: 'auto' }}>
            Zoom: {zoomLabel}
          </Typography>
        </Stack>
        <Box
          ref={areaRef}
          tabIndex={0}
          role="region"
          aria-label="Immagine della schermata: scorri con le frecce; + e - per lo zoom, 0 per adattarla"
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            bgcolor: 'action.hover',
            display: 'flex',
            // Adattata: centrata; ingrandita: dall'angolo, così lo scorrimento raggiunge ogni bordo.
            alignItems: zoom === 'fit' ? 'center' : 'flex-start',
            justifyContent: zoom === 'fit' ? 'center' : 'flex-start',
            '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
          }}
        >
          {unreadable ? (
            <Alert severity="error" role="alert" sx={{ m: 4, alignSelf: 'flex-start' }}>
              Immagine non leggibile: il file scaricato non si può mostrare.
            </Alert>
          ) : (
            <Box
              component="img"
              src={src}
              alt={`Schermata catturata all'invio del ticket ${code}`}
              onLoad={(event) => {
                const image = event.currentTarget;
                const size = { width: image.naturalWidth, height: image.naturalHeight };
                setNatural(size);
                measureFit(size);
              }}
              onError={() => setUnreadable(true)}
              sx={
                zoom === 'fit'
                  ? { display: 'block', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }
                  : {
                      display: 'block',
                      maxWidth: 'none',
                      flexShrink: 0,
                      width: natural ? natural.width * zoom : 'auto',
                      height: 'auto',
                    }
              }
            />
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
}
